import React, { useCallback, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { ChatToolCall } from "../types/chat.types";
import {
  formatConfirmationArguments,
  isDestructiveToolCall,
  toolConfirmationTitle,
} from "../utils/toolConfirmCopy";

export interface ToolConfirmationCardProps {
  toolCall: ChatToolCall;
  turnId: string | null;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onApprove: () => void;
  onReject: () => void;
}

/** Tab cycles only between enabled action buttons (focus trap for alertdialog). */
function focusNextActionButton(
  e: React.KeyboardEvent,
  actions: HTMLButtonElement[]
): void {
  if (e.key !== "Tab" || actions.length === 0) return;
  e.preventDefault();
  const active = document.activeElement;
  const idx = actions.findIndex((el) => el === active);
  const nextIdx = e.shiftKey
    ? idx <= 0
      ? actions.length - 1
      : idx - 1
    : idx < 0 || idx >= actions.length - 1
      ? 0
      : idx + 1;
  actions[nextIdx]?.focus();
}

const ToolConfirmationCard: React.FC<ToolConfirmationCardProps> = ({
  toolCall,
  turnId,
  isSubmitting = false,
  errorMessage,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const rejectRef = useRef<HTMLButtonElement>(null);
  const approveRef = useRef<HTMLButtonElement>(null);
  const destructive = isDestructiveToolCall(toolCall);
  const missingTurnId = !turnId;

  const actionButtons = useCallback((): HTMLButtonElement[] => {
    return [rejectRef.current, approveRef.current].filter(
      (el): el is HTMLButtonElement => el != null && !el.disabled
    );
  }, []);

  useEffect(() => {
    rejectRef.current?.focus();
  }, [toolCall.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab") {
        focusNextActionButton(e, actionButtons());
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (!missingTurnId && !isSubmitting) onApprove();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (!isSubmitting) onReject();
      }
    },
    [actionButtons, isSubmitting, missingTurnId, onApprove, onReject]
  );

  const body =
    toolCall.preview?.trim() ||
    formatConfirmationArguments(toolCall.name, toolCall.arguments ?? {})
      .map((row) => `${row.label}: ${row.value}`)
      .join("\n");

  return (
    <div
      ref={cardRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onKeyDown={handleKeyDown}
      className={`mx-4 mb-3 rounded-lg border px-4 py-3 shadow-sm ${
        destructive ? "border-red-300 bg-red-50" : "border-amber-200 bg-amber-50"
      }`}
      data-testid="tool-confirmation-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p id={titleId} className="text-sm font-semibold text-gray-900">
            🔧 {toolConfirmationTitle(toolCall.name)}
          </p>
          <p className="text-xs text-gray-600">
            {destructive ? t("Destructive action — confirm to run") : t("Confirm to run")}
          </p>
        </div>
        <span className="font-mono text-[10px] text-gray-500">{toolCall.name}</span>
      </div>
      <pre
        id={descId}
        className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white/80 p-2 text-xs text-gray-800"
      >
        {body}
      </pre>
      {missingTurnId ? (
        <p className="mt-2 text-xs font-medium text-red-700" data-testid="tool-confirm-missing-turn">
          {t("Cannot approve — turn id missing. Retry the message or contact support.")}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="mt-2 text-xs text-red-700" data-testid="tool-confirm-error">
          {errorMessage}
        </p>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <button
          ref={rejectRef}
          type="button"
          data-testid="tool-confirm-reject"
          disabled={isSubmitting}
          onClick={onReject}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {t("Reject")}
        </button>
        <button
          ref={approveRef}
          type="button"
          data-testid="tool-confirm-approve"
          disabled={isSubmitting || missingTurnId}
          onClick={onApprove}
          className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
            destructive ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {t("Approve")}
        </button>
      </div>
    </div>
  );
};

export default ToolConfirmationCard;
